<template>
  <!-- ADD / EDIT -->
  <div
    class="modal fade"
    id="new-employee-salary"
    tabindex="-1"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable"
    >
      <div class="modal-content dq-modal shadow-lg border-0">
        <!-- Header -->
        <div class="modal-header dq-header border-0">
          <div class="d-flex align-items-start gap-3 w-100">
            <div class="dq-icon">
              <i class="ti ti-cash-banknote"></i>
            </div>

            <div class="flex-grow-1">
              <div
                class="d-flex align-items-center justify-content-between gap-3"
              >
                <div>
                  <h4 class="modal-title mb-1">
                    {{
                      isEdit ? "Edit Employee Salary" : "Add Employee Salary"
                    }}
                  </h4>
                  <div class="text-muted small">
                    Capture allowances, benefits, deductions — preview statutory
                    results instantly.
                    <span class="d-none d-md-inline">
                      • <span class="dq-kbd">Ctrl</span>+<span class="dq-kbd"
                        >Enter</span
                      >
                      to save • <span class="dq-kbd">Ctrl</span>+<span
                        class="dq-kbd"
                        >Shift</span
                      >+<span class="dq-kbd">A</span>/<span class="dq-kbd"
                        >B</span
                      >/<span class="dq-kbd">D</span> to add lines
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  class="btn dq-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  @click="resetForm"
                  title="Close (Esc)"
                >
                  <i class="ti ti-x"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <form @submit.prevent="submitForm" novalidate>
          <div class="modal-body pt-3">
            <!-- Sticky totals (top) -->
            <div class="dq-sticky-top">
              <div class="dq-sticky-inner">
                <span class="dq-chip">
                  Gross
                  <strong class="ms-1">{{ money(animated.gross) }}</strong>
                </span>
                <span class="dq-chip dq-chip-red">
                  Deductions
                  <strong class="ms-1">{{
                    money(animated.totalDeductions)
                  }}</strong>
                </span>
                <span class="dq-chip dq-chip-green">
                  Net <strong class="ms-1">{{ money(animated.net) }}</strong>
                </span>
              </div>
            </div>

            <!-- Top info panel -->
            <div class="dq-surface p-3 p-md-4 mb-3">
              <div class="row g-3 align-items-end">
                <div class="col-lg-6">
                  <label class="form-label dq-label">
                    Employee <span class="text-danger">*</span>
                  </label>

                  <div class="dropdown w-100 dq-emp-dd">
                    <button
                      id="employee-dd-btn"
                      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dd-btn"
                      type="button"
                      data-bs-toggle="dropdown"
                      :disabled="isEdit"
                      @click="onEmployeeDropdownOpen"
                      :class="{ 'dq-dd-invalid': showEmployeeInvalid }"
                    >
                      <span class="text-truncate">
                        {{ selectedEmployeeLabel }}
                      </span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div
                      class="dropdown-menu p-2 w-100 shadow dq-dd-menu"
                      @click.stop
                    >
                      <input
                        ref="employeeSearchRef"
                        v-model="employeeQuery"
                        type="text"
                        class="form-control mb-2 dq-input"
                        placeholder="Search employee name or ID..."
                        autocomplete="off"
                      />

                      <div class="dq-search-list">
                        <button
                          v-for="opt in filteredEmployees"
                          :key="opt.id"
                          type="button"
                          class="dropdown-item text-truncate dq-dd-item"
                          @click="chooseEmployee(opt)"
                        >
                          <div class="fw-semibold text-truncate">
                            {{ opt.text }}
                          </div>
                          <!-- <div class="small text-muted text-truncate">
                            ID: {{ opt.id }}
                          </div> -->
                        </button>

                        <div
                          v-if="filteredEmployees.length === 0"
                          class="text-center py-2 small text-muted"
                        >
                          No employee found
                        </div>
                      </div>

                      <div
                        class="d-flex justify-content-between align-items-center pt-2 border-top mt-2"
                      >
                        <button
                          type="button"
                          class="btn btn-sm btn-light"
                          @click="clearEmployee"
                        >
                          Clear
                        </button>

                        <span class="small text-muted">
                          {{ filteredEmployees.length }} result(s)
                        </span>
                      </div>
                    </div>

                    <!-- hidden required input so native validation works -->
                    <input
                      v-model="form.employee_id"
                      type="text"
                      class="dq-hidden-required"
                      required
                      tabindex="-1"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="small text-muted mt-1">
                    Tip: type employee number or name. Press
                    <span class="dq-kbd">Esc</span> to close modal.
                  </div>

                  <div
                    v-if="selectedEmployee"
                    class="dq-payroll-context mt-2"
                  >
                    <span class="badge dq-payroll-badge">
                      Pay: {{ payFrequencyLabel }}
                    </span>
                    <span class="badge dq-payroll-badge">
                      Basis: {{ salaryBasisLabel }}
                    </span>
                    <span
                      v-if="previewScalingActive"
                      class="badge dq-payroll-badge dq-payroll-badge-soft"
                    >
                      Preview: {{ previewPeriodLabel }}
                    </span>
                  </div>
                </div>

                <div class="col-lg-6">
                  <label class="form-label dq-label">
                    Basic Salary <span class="text-danger">*</span>
                  </label>

                  <div class="input-group dq-inputgroup">
                    <span class="input-group-text dq-addon_currency">
                      {{ companyCurrencySymbol }}
                      <!-- <i class="ti ti-currency-dollar"></i> -->
                    </span>
                    <input
                      v-model.number="form.basic_salary"
                      type="number"
                      min="0"
                      class="form-control dq-input"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div class="small text-muted mt-1">
                    {{ basicSalaryGuidance.primary }}
                  </div>
                  <div
                    v-if="basicSalaryGuidance.secondary"
                    class="small text-muted"
                  >
                    {{ basicSalaryGuidance.secondary }}
                  </div>
                </div>

                <div
                  v-if="showAdvanceFixedAmount"
                  class="col-lg-6"
                >
                  <label class="form-label dq-label">
                    Mid-Month Advance Amount
                    <span
                      v-if="advanceCalculationMode === 'fixed'"
                      class="text-danger"
                    >*</span>
                  </label>

                  <div class="input-group dq-inputgroup">
                    <span class="input-group-text dq-addon_currency">
                      {{ companyCurrencySymbol }}
                    </span>
                    <input
                      v-model.number="form.advance_fixed_amount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control dq-input"
                      :required="advanceCalculationMode === 'fixed'"
                      placeholder="0.00"
                    />
                  </div>

                  <div class="small text-muted mt-1">
                    {{ advanceFixedAmountHelp }}
                  </div>
                </div>
              </div>

              <div
                v-if="selectedEmployee && !payrollFrequencyContext.supported"
                class="alert alert-warning py-2 px-3 mb-0 mt-3"
              >
                {{ unsupportedSalaryBasisMessage }}
              </div>

              <div
                v-else-if="previewScalingActive"
                class="dq-payroll-note mt-3"
              >
                <i class="ti ti-info-circle me-1"></i>
                Allowances, benefits, and deductions use the same salary basis
                as basic salary. The preview below shows estimated amounts for
                one {{ previewPeriodLabel }}; stored salary values are kept as
                entered for payroll.
              </div>
            </div>

            <!-- 3 columns -->
            <div class="row g-3 mb-3">
              <!-- Allowances column -->
              <div class="col-lg-4">
                <div class="dq-card h-100">
                  <div class="dq-card-head dq-allow">
                    <div class="d-flex align-items-center gap-2">
                      <i class="ti ti-plus"></i>
                      <div>
                        <div class="dq-title">Allowances / Payments</div>
                        <div class="dq-subtitle">
                          Add recurring & adhoc allowances
                        </div>
                      </div>
                    </div>

                    <div class="dq-pill">
                      Extra:
                      <strong class="ms-1">{{
                        money(extraAllowancesTotal)
                      }}</strong>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <!-- Predefined allowances -->
                    <div class="dq-block">
                      <label class="form-label dq-label">House Allowance</label>
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-home"></i
                        ></span>
                        <input
                          v-model.number="form.house_allowance"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div class="dq-block">
                      <label class="form-label dq-label"
                        >Transport Allowance</label
                      >
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-car"></i
                        ></span>
                        <input
                          v-model.number="form.transport_allowance"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div class="dq-block">
                      <label class="form-label dq-label"
                        >Other Allowances</label
                      >
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-dots"></i
                        ></span>
                        <input
                          v-model.number="form.other_allowance"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                      <div class="dq-help">
                        Base “other allowances” amount (before extra lines
                        below).
                      </div>
                    </div>

                    <!-- Extra lines header -->
                    <div class="dq-lines-head">
                      <div class="d-flex align-items-center gap-2">
                        <span class="dq-lines-title">Additional lines</span>
                        <span class="dq-lines-hint">Name + Amount </span>
                      </div>

                      <button
                        type="button"
                        class="btn btn-sm dq-btn-add"
                        @click="addExtraAllowance"
                        title="Ctrl+Shift+A"
                      >
                        <i class="ti ti-plus me-1"></i> Add line
                      </button>
                    </div>

                    <div v-if="!extraAllowances.length" class="dq-empty">
                      <i class="ti ti-list-details"></i>
                      <div class="mt-2 fw-semibold">No extra allowances</div>
                      <div class="small text-muted">
                        Add custom allowances like “Overtime”, “Bonus”, etc.
                      </div>
                    </div>

                    <div
                      v-for="(item, index) in extraAllowances"
                      :key="'allowance-' + index"
                      class="dq-line"
                    >
                      <input
                        ref="allowanceLabelRefs"
                        v-model="item.label"
                        type="text"
                        class="form-control form-control-sm dq-input"
                        placeholder="Allowance name"
                        :data-line-type="'allowance'"
                        :data-index="index"
                        @keydown.enter.prevent="onLineEnter('allowance', index)"
                      />
                      <input
                        v-model.number="item.amount"
                        type="number"
                        min="0"
                        class="form-control form-control-sm dq-input"
                        placeholder="Amount"
                      />
                      <button
                        type="button"
                        class="btn btn-sm dq-btn-x"
                        @click="removeExtraAllowance(index)"
                        title="Remove"
                      >
                        <i class="ti ti-x"></i>
                      </button>
                    </div>

                    <div class="dq-total mt-3">
                      <span class="text-muted small"
                        >Total extra allowances</span
                      >
                      <span class="fw-bold">{{
                        money(extraAllowancesTotal)
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Benefits column -->
              <div class="col-lg-4">
                <div class="dq-card h-100">
                  <div class="dq-card-head dq-benefit">
                    <div class="d-flex align-items-center gap-2">
                      <i class="ti ti-gift"></i>
                      <div>
                        <div class="dq-title">Benefits</div>
                        <div class="dq-subtitle">
                          Non-salary earnings (taxable)
                        </div>
                      </div>
                    </div>

                    <div class="dq-pill">
                      Total:
                      <strong class="ms-1">{{ money(benefitsTotal) }}</strong>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="dq-callout mb-3">
                      <i class="ti ti-info-circle"></i>
                      <div>
                        <div class="fw-semibold">P9-ready classification</div>
                        <div class="small text-muted">
                          Classify benefits for reporting (non-cash, quarters,
                          etc.).
                        </div>
                      </div>
                    </div>

                    <div class="dq-lines-head">
                      <div class="d-flex align-items-center gap-1">
                        <span class="dq-lines-title">Benefit lines</span>
                        <span class="dq-lines-hint"
                          >Name + Amount + Classification
                        </span>
                      </div>

                      <button
                        type="button"
                        class="btn btn-sm dq-btn-add"
                        @click="addBenefit"
                        title="Ctrl+Shift+B"
                      >
                        <i class="ti ti-plus me-1"></i> Add line
                      </button>
                    </div>

                    <div v-if="!benefits.length" class="dq-empty">
                      <i class="ti ti-mood-smile"></i>
                      <div class="mt-2 fw-semibold">No benefits added</div>
                      <div class="small text-muted">
                        Examples: Airtime, Fuel Card, Lunch, Medical cover.
                      </div>
                    </div>

                    <div
                      v-for="(item, index) in benefits"
                      :key="'benefit-' + index"
                      class="dq-benefit-row"
                    >
                      <div class="dq-line">
                        <input
                          ref="benefitLabelRefs"
                          v-model="item.label"
                          type="text"
                          class="form-control form-control-sm dq-input"
                          placeholder="Benefit name (e.g. Fuel Card)"
                          :data-line-type="'benefit'"
                          :data-index="index"
                          @keydown.enter.prevent="onLineEnter('benefit', index)"
                        />
                        <input
                          v-model.number="item.amount"
                          type="number"
                          min="0"
                          class="form-control form-control-sm dq-input"
                          placeholder="Amount"
                        />
                        <button
                          type="button"
                          class="btn btn-sm dq-btn-x"
                          @click="removeBenefit(index)"
                          title="Remove"
                        >
                          <i class="ti ti-x"></i>
                        </button>
                      </div>
                      <!-- <div class="dq-mini">
                        <span class="text-muted small">P9 classification</span>

                        <div class="dropdown dq-mini-dd">
                          <button
                            class="btn btn-outline-secondary btn-sm w-100 d-flex justify-content-between align-items-center dq-dd-btn"
                            type="button"
                            data-bs-toggle="dropdown"
                          >
                            <span class="text-truncate">{{
                              p9Label(item.p9_type)
                            }}</span>
                            <i class="ti ti-chevron-down ms-2"></i>
                          </button>

                          <div
                            class="dropdown-menu p-2 shadow dq-dd-menu"
                            @click.stop
                          >
                            <button
                              v-for="opt in p9Options"
                              :key="String(opt.value)"
                              type="button"
                              class="dropdown-item dq-dd-item"
                              @click="setP9(item, opt.value)"
                            >
                              <div class="fw-semibold">{{ opt.label }}</div>
                            </button>
                          </div>
                        </div>
                      </div> -->

                      <P9Select
                        v-model="item.p9_type"
                        class="mt-1"
                        :id="`benefit-${index}`"
                      />
                    </div>

                    <div class="dq-total mt-3">
                      <span class="text-muted small">Total benefits</span>
                      <span class="fw-bold">{{ money(benefitsTotal) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Deductions column -->
              <div class="col-lg-4">
                <div class="dq-card h-100">
                  <div class="dq-card-head dq-deduct">
                    <div class="d-flex align-items-center gap-2">
                      <i class="ti ti-minus"></i>
                      <div>
                        <div class="dq-title">Deductions</div>
                        <div class="dq-subtitle">
                          Manual deductions plus auto loan/savings context
                        </div>
                      </div>
                    </div>

                    <div class="dq-pill">
                      Manual extra:
                      <strong class="ms-1">{{
                        money(extraDeductionsTotal)
                      }}</strong>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <!-- AUTO META WARNING (Loans/Savings engine) -->
                    <div
                      v-if="metaHint.has_meta"
                      class="dq-callout mb-3 dq-callout-warn"
                    >
                      <i class="ti ti-lock"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold">
                          Auto Loans/Savings is active for this employee.
                        </div>
                        <div class="small text-muted">
                          Manual loan/savings fields are read-only to avoid
                          double deductions.
                          <span v-if="metaHint.counts?.loans">
                            Loans: <strong>{{ metaHint.counts.loans }}</strong
                            >.</span
                          >
                          <span v-if="metaHint.counts?.savings">
                            Savings:
                            <strong>{{ metaHint.counts.savings }}</strong
                            >.</span
                          >
                        </div>

                        <div
                          class="small mt-2"
                          v-if="
                            metaHint.active?.loans?.length ||
                            metaHint.active?.savings?.length
                          "
                        >
                          <div
                            v-if="metaHint.active.loans?.length"
                            class="mb-1"
                          >
                            <strong>Active Loans</strong>
                            <!-- <div class="text-muted">
                              <span
                                v-for="(l, i) in metaHint.active.loans"
                                :key="'ml' + i"
                                class="me-2"
                              >
                                • {{ l.loan_type_name|| l.type || "Loan" }} ({{
                                  l.payment_method || "straight_line"
                                }})
                              </span>
                            </div> -->

                             <div class="text-muted">
                              <span
                                v-for="(l, i) in metaHint.active.loans"
                                :key="'ml' + i"
                                class="me-2"
                              >
                                • {{ l.loan_type_name|| l.type || "Loan" }} 
                              </span>
                            </div>
                          </div>
                          <div v-if="metaHint.active.savings?.length">
                            <strong>Active Savings</strong>
                            <!-- <div class="text-muted">
                              <span
                                v-for="(s, i) in metaHint.active.savings"
                                :key="'ms' + i"
                                class="me-2"
                              >
                                • {{ s.name || s.type || "Saving" }} ({{
                                  Number(
                                    s.monthly_contribution || 0,
                                  ).toLocaleString()
                                }})
                              </span>
                            </div> -->
                          

                               <div class="text-muted">
                              <span
                                v-for="(s, i) in metaHint.active.savings"
                                :key="'ms' + i"
                                class="me-2"
                              >
                                • {{ s.name || s.type || "Saving" }} 
                              </span>
                            </div>
                          </div>
                        </div>

                        <div class="mt-2">
                          <router-link
                            class="btn btn-sm btn-outline-dark"
                            to="/savings-management/savings"
                          >
                            <i class="ti ti-pig-money me-1"></i> Manage Savings
                            Accounts
                          </router-link>
                        </div>
                      </div>
                    </div>
                    <!-- Predefined deductions -->
                    <div class="dq-block">
                      <label class="form-label dq-label"
                        >Pension Contribution (Employee, optional)</label
                      >
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-pig-money"></i
                        ></span>
                        <input
                          v-model.number="form.pension_contrib"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div class="dq-block">
                      <label class="form-label dq-label"
                        >Insurance Premium Paid</label
                      >
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-shield-check"></i
                        ></span>
                        <input
                          v-model.number="form.insurance_premium"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div class="dq-block">
                      <label class="form-label dq-label"
                        >NSSF Voluntary (Employee top-up)</label
                      >
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-building-bank"></i
                        ></span>
                        <input
                          v-model.number="form.nssf_voluntary"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                      <div class="dq-help">
                        Extra employee NSSF contribution. This reduces net pay and
                        feeds the monthly NSSF export, but does not change the
                        statutory NSSF calculation itself.
                      </div>
                    </div>

                    <div class="dq-block">
                      <label class="form-label dq-label"
                        >Mortgage Interest Paid</label
                      >
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-building"></i
                        ></span>
                        <input
                          v-model.number="form.mortgage_interest"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div class="dq-block">
                      <label class="form-label dq-label"
                        >Other Loans (Mannual)</label
                      >
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-cash"></i
                        ></span>
                        <input
                          v-model.number="form.other_loans"
                          type="number"
                          min="0"
                          :disabled="disableLoanSavingsFields"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                      <div class="dq-help">
                        Base loan amount (before additional manual deduction lines below).
                      </div>
                    </div>

                    <div class="dq-block">
                      <label class="form-label dq-label">HELB Deduction</label>
                      <div class="input-group dq-inputgroup">
                        <span class="input-group-text dq-addon"
                          ><i class="ti ti-school"></i
                        ></span>
                        <input
                          v-model.number="form.helb"
                          :disabled="disableLoanSavingsFields"
                          type="number"
                          min="0"
                          class="form-control dq-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <!-- Extra deduction lines -->
                    <div class="dq-lines-head mt-2">
                      <div class="d-flex align-items-center gap-2">
                        <span class="dq-lines-title">Additional manual lines</span>
                        <span class="dq-lines-hint"
                          >Name + Amount + Classification</span
                        >
                      </div>

                      <button
                        type="button"
                        class="btn btn-sm dq-btn-add"
                        @click="addExtraDeduction"
                        title="Ctrl+Shift+D"
                      >
                        <i class="ti ti-plus me-1"></i> Add line
                      </button>
                    </div>

                    <div v-if="!extraDeductions.length" class="dq-empty">
                      <i class="ti ti-receipt-tax"></i>
                      <div class="mt-2 fw-semibold">No additional manual deductions</div>
                      <div class="small text-muted">
                        Add items like “PRMF”, “Welfare”, “Union fees”, etc.
                      </div>
                    </div>

                    <div
                      v-for="(item, index) in extraDeductions"
                      :key="'deduction-' + index"
                      class="dq-benefit-row"
                    >
                      <div class="dq-line">
                        <input
                          ref="deductionLabelRefs"
                          v-model="item.label"
                          type="text"
                          class="form-control form-control-sm dq-input"
                          placeholder="Deduction name"
                          :data-line-type="'deduction'"
                          :data-index="index"
                          @keydown.enter.prevent="
                            onLineEnter('deduction', index)
                          "
                        />
                        <input
                          v-model.number="item.amount"
                          type="number"
                          min="0"
                          class="form-control form-control-sm dq-input"
                          placeholder="Amount"
                        />
                        <button
                          type="button"
                          class="btn btn-sm dq-btn-x"
                          @click="removeExtraDeduction(index)"
                          title="Remove"
                        >
                          <i class="ti ti-x"></i>
                        </button>
                      </div>

                      <!-- <div class="dq-mini">
                        <span class="text-muted small">P9 classification</span>
                        <select
                          v-model="item.p9_type"
                          class="form-select form-select-sm dq-select"
                        >
                          <option :value="null">
                            Normal deduction (default)
                          </option>
                          <option value="prmf">
                            Post-Retirement Medical Fund
                          </option>
                        </select>
                      </div> -->

                      <P9Select
                        v-model="item.p9_type"
                        :id="`deduction-${index}`"
                        label="P9 classification"
                        :options="[
                          { label: 'Normal deduction (default)', value: null },
                          {
                            label: 'Post-Retirement Medical Fund',
                            value: 'prmf',
                          },
                        ]"
                      />
                    </div>

                    <div class="dq-total mt-3">
                      <span class="text-muted small"
                        >Total additional manual deductions</span
                      >
                      <span class="fw-bold">{{
                        money(extraDeductionsTotal)
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statutory Calculations & Summary -->
            <div class="dq-surface p-3 p-md-4">
              <div
                class="d-flex align-items-center justify-content-between gap-3 mb-3"
              >
                <div class="d-flex align-items-center gap-2">
                  <div class="dq-icon-sm">
                    <i class="ti ti-report-money"></i>
                  </div>
                  <div>
                    <div class="fw-bold">Statutory Calculations & Summary</div>
                    <div class="small text-muted">
                      <template v-if="previewScalingActive">
                        Live preview per {{ previewPeriodLabel }} (country:
                        <strong>{{ effectiveCountry }}</strong
                        >)
                      </template>
                      <template v-else>
                        Live preview (country:
                        <strong>{{ effectiveCountry }}</strong
                        >)
                      </template>
                    </div>
                  </div>
                </div>

                <div class="dq-badges">
                  <span class="badge dq-badge">
                    Gross:
                    <strong class="ms-1">{{ money(animated.gross) }}</strong>
                  </span>
                  <span class="badge dq-badge dq-badge-success">
                    Net: <strong class="ms-1">{{ money(animated.net) }}</strong>
                  </span>
                </div>
              </div>

              <div class="row g-2 dq-kpi">
                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">Gross Salary</span>
                    <span class="dq-kpi-value">{{
                      money(animated.gross)
                    }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">NSSF (Employee)</span>
                    <span class="dq-kpi-value">{{
                      money(animated.nssfEmployee)
                    }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">NSSF (Employer)</span>
                    <span class="dq-kpi-value">{{
                      money(animated.nssfEmployer)
                    }}</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">SHIF</span>
                    <span class="dq-kpi-value">{{ money(animated.shif) }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">Housing Levy (Employee)</span>
                    <span class="dq-kpi-value">{{
                      money(animated.ahlEmployee)
                    }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">HELB</span>
                    <span class="dq-kpi-value">{{ money(animated.helb) }}</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">Taxable Income</span>
                    <span class="dq-kpi-value">{{
                      money(animated.taxableIncome)
                    }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="dq-kpi-item">
                    <span class="dq-kpi-label">PAYE</span>
                    <span class="dq-kpi-value">{{ money(animated.paye) }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="dq-kpi-item dq-kpi-danger">
                    <span class="dq-kpi-label">Total Deductions</span>
                    <span class="dq-kpi-value">{{
                      money(animated.totalDeductions)
                    }}</span>
                  </div>
                </div>

                <div class="col-md-12">
                  <div class="dq-meta mt-2">
                    <div class="dq-meta-item">
                      <span class="text-muted small">Extra allowances</span>
                      <strong>{{ money(extraAllowancesTotal) }}</strong>
                    </div>
                    <div class="dq-meta-item">
                      <span class="text-muted small">Benefits</span>
                      <strong>{{ money(benefitsTotal) }}</strong>
                    </div>
                    <div class="dq-meta-item">
                      <span class="text-muted small">Extra deductions</span>
                      <strong>{{ money(extraDeductionsTotal) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sticky mini summary (bottom) -->
              <div class="dq-sticky mt-3">
                <div class="dq-sticky-inner">
                  <div class="dq-sticky-left">
                    <span class="dq-dot"></span>
                    <span class="small text-muted">Preview</span>
                  </div>
                  <div class="dq-sticky-right">
                    <span class="dq-chip">
                      Gross
                      <strong class="ms-1">{{ money(animated.gross) }}</strong>
                    </span>
                    <span class="dq-chip dq-chip-red">
                      Deductions
                      <strong class="ms-1">{{
                        money(animated.totalDeductions)
                      }}</strong>
                    </span>
                    <span class="dq-chip dq-chip-green">
                      Net Pay
                      <strong class="ms-1">{{ money(animated.net) }}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer border-0">
            <button
              type="button"
              class="btn dq-btn-secondary"
              data-bs-dismiss="modal"
              @click="resetForm"
            >
              Cancel
            </button>

            <button
              :disabled="submitting"
              type="submit"
              class="btn dq-btn-primary"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ isEdit ? "Update Salary" : "Add Employee Salary" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE CONFIRM -->
  <div class="modal fade" id="delete_modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content dq-modal shadow-lg border-0">
        <div class="modal-header dq-header border-0">
          <div class="d-flex align-items-center gap-2">
            <div class="dq-icon-sm dq-danger">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h5 class="modal-title mb-0">Delete Salary</h5>
              <div class="small text-muted">This action cannot be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="dq-surface p-3">
            <p class="mb-0">
              Are you sure you want to delete this employee's salary record?
              <br />
              <strong v-if="employeeToDeleteName">{{
                employeeToDeleteName
              }}</strong>
            </p>
          </div>
        </div>

        <div class="modal-footer dq-footer border-0">
          <button class="btn dq-btn-secondary" data-bs-dismiss="modal">
            Cancel
          </button>
          <button
            class="btn dq-btn-danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  reactive,
} from "vue";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import { usePayrollConfigStore } from "@/stores/payrollConfigStore";
import { storeToRefs } from "pinia";
import { toast } from "@/utils/toast";
import { useCompanyStore } from "@/stores/companyStore";
import P9Select from "@/components/shared/P9Select.vue";
import { Modal } from "bootstrap";
import {
  buildFrequencyPreviewContext,
  getBasicSalaryGuidance,
  getPayFrequencyLabel,
  getPreviewPeriodLabel,
  getSalaryBasisLabel,
  getUnsupportedSalaryBasisMessage,
  scaleStatutoryToPeriod,
  scaleStoredToCalculator,
  scaleStoredToPeriod,
} from "@/utils/payrollFrequencyPreview";
import {
  currentPayrollMonthStart,
  currentPayrollYYYYMM,
  normalizePayrollMonthStart,
  toYYYYMM,
} from "@/utils/payrollPeriodHelpers";


const props = defineProps({
  employeeSalary: { type: Object, default: null },
  employeeToDelete: { type: Object, default: null },
});
const emit = defineEmits(["refresh"]);

// Stores
const salaryStore = useEmployeeSalaryStore();
const employeeStore = useEmployeeStore();
const payrollConfigStore = usePayrollConfigStore();
const { employees } = storeToRefs(employeeStore);
const { config } = storeToRefs(payrollConfigStore);

const companyStore = useCompanyStore();
const { companies, activeCompanyId } = storeToRefs(companyStore);

const activeCompany = computed(() =>
  companies.value.find((c) => c.id === activeCompanyId.value),
);

const showEmployeeInvalid = ref(false);

const companyCurrencySymbol = computed(() => {
  const c = activeCompany.value;
  return c?.currency_symbol || c?.currency_code || "KES";
});

function money(val) {
  return (
    companyCurrencySymbol.value +
    " " +
    Number(val ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

const p9Options = [
  { label: "Normal benefit (default)", value: null },
  { label: "Non-cash benefit", value: "benefit_noncash" },
  { label: "Accommodation / quarters", value: "quarters" },
];

function p9Label(val) {
  const found = p9Options.find((o) => o.value === val);
  return found?.label || "Normal benefit (default)";
}

function setP9(item, value) {
  item.p9_type = value;
  // close dropdown like you already do
  document.body.click();
}

const isEdit = computed(() => !!props.employeeSalary);

const emptyForm = () => ({
  employee_id: "",
  payroll_month: currentPayrollMonthStart(),
  basic_salary: 0,
  house_allowance: 0,
  transport_allowance: 0,
  other_allowance: 0,
  pension_contrib: 0,
  insurance_premium: 0,
  nssf_voluntary: 0,
  mortgage_interest: 0,
  // sacco_loan: 0,
  other_loans: 0,

  helb: 0,
  advance_fixed_amount: null,
});

const form = ref(emptyForm());
const submitting = ref(false);
const deleting = ref(false);

// Dynamic user-defined lines
const extraAllowances = ref([]);
const benefits = ref([]);
const extraDeductions = ref([]);

// Refs for auto-focus
const allowanceLabelRefs = ref([]);
const benefitLabelRefs = ref([]);
const deductionLabelRefs = ref([]);

const employeeQuery = ref("");
const employeeSearchRef = ref(null);

const selectedEmployeeLabel = computed(() => {
  const id = Number(form.value.employee_id || 0);
  if (!id) return "Search & select employee";
  const match = employeeOptions.value.find((x) => Number(x.id) === id);
  if (match?.text) return match.text;
  const employee = employees.value.find((e) => Number(e.id) === id);
  return employee
    ? `${employee.first_name} ${employee.last_name || ""} (${employee.employee_id})`
    : "Selected employee";
});

const selectedEmployee = computed(() => {
  const id = Number(form.value.employee_id || 0);
  if (!id) return null;
  return employees.value.find((e) => Number(e.id) === id) || null;
});

const payrollFrequencyContext = computed(() =>
  buildFrequencyPreviewContext(selectedEmployee.value, config.value ?? {}),
);

const advanceCalculationMode = computed(
  () => config.value?.advance?.calculation_mode || "fixed",
);

const showAdvanceFixedAmount = computed(() =>
  ["fixed", "hybrid"].includes(advanceCalculationMode.value),
);

const advanceFixedAmountHelp = computed(() => {
  if (advanceCalculationMode.value === "fixed") {
    return "Mid-month cash advance for this employee, recovered in full on that month’s Final payroll. Enter 0 if none. For multi-month repayment, use Employee Loans.";
  }
  return `Optional. When set, this amount is used instead of the company default (${config.value?.advance?.default_percent_of_basic ?? 50}% of basic) for mid-month advance (full same-month recovery). Leave empty to use the percentage.`;
});

const previewScalingActive = computed(
  () =>
    !!selectedEmployee.value &&
    !payrollFrequencyContext.value.isMonthly &&
    payrollFrequencyContext.value.supported,
);

const previewRunScale = computed(() =>
  previewScalingActive.value ? payrollFrequencyContext.value.runScale : 1,
);

const previewMonthlyFactor = computed(() =>
  previewScalingActive.value
    ? payrollFrequencyContext.value.monthlyEquivalentFactor
    : 1,
);

const payFrequencyLabel = computed(() =>
  getPayFrequencyLabel(payrollFrequencyContext.value.frequency),
);

const salaryBasisLabel = computed(() =>
  getSalaryBasisLabel(payrollFrequencyContext.value.salaryBasis),
);

const previewPeriodLabel = computed(() =>
  getPreviewPeriodLabel(payrollFrequencyContext.value.frequency),
);

const basicSalaryGuidance = computed(() =>
  selectedEmployee.value
    ? getBasicSalaryGuidance(payrollFrequencyContext.value)
    : {
        primary:
          "Select an employee first. Basic salary meaning depends on their pay frequency and salary basis.",
        secondary:
          "Monthly employees enter monthly amounts. Weekly or daily employees may enter monthly-equivalent or per-period amounts.",
      },
);

const unsupportedSalaryBasisMessage = computed(() =>
  getUnsupportedSalaryBasisMessage(payrollFrequencyContext.value),
);

function toCalculatorAmount(stored) {
  if (!previewScalingActive.value) return Number(stored) || 0;
  return scaleStoredToCalculator(
    stored,
    previewRunScale.value,
    previewMonthlyFactor.value,
  );
}

function toPeriodAmount(stored) {
  if (!previewScalingActive.value) return Number(stored) || 0;
  return scaleStoredToPeriod(stored, previewRunScale.value);
}

function statutoryToPeriod(calculated) {
  if (!previewScalingActive.value) return Number(calculated) || 0;
  return scaleStatutoryToPeriod(calculated, previewMonthlyFactor.value);
}

const filteredEmployees = computed(() => {
  const q = employeeQuery.value.trim().toLowerCase();
  if (!q) return employeeOptions.value;
  return employeeOptions.value.filter((e) => {
    const t = (e.text || "").toLowerCase();
    const id = String(e.id || "");
    return t.includes(q) || id.includes(q);
  });
});

function chooseEmployee(opt) {
  form.value.employee_id = opt.id;
  employeeQuery.value = "";
  showEmployeeInvalid.value = false; // ✅ clear error state
  closeBootstrapDropdown();
}

function clearEmployee() {
  form.value.employee_id = "";
  employeeQuery.value = "";
}

function onEmployeeDropdownOpen() {
  nextTick(() => {
    employeeSearchRef.value?.focus();
  });
}

// closes only the dropdown (not the modal)
function closeBootstrapDropdown() {
  // Bootstrap 5 dropdown closes when clicking outside.
  // We can simulate an outside click safely:
  document.body.click();
}

// ---- Defaults mirroring backend PayrollConfigService (for safety) ----
const defaultConfig = {
  country: "KE",
  nssf: {
    lower_limit: 8000,
    upper_limit: 72000,
    rate: 0.06,
    employee_cap: 4320,
    employer_match: true,
  },
  shif: {
    rate: 0.0275,
    min: 300,
  },
  ahl: {
    rate: 0.015,
  },
  paye: {
    bands: [
      { up_to: 24000, rate: 0.1 },
      { up_to: 32333, rate: 0.25 },
      { up_to: 500000, rate: 0.3 },
      { up_to: 800000, rate: 0.325 },
    ],
    top_rate: 0.35,
    personal_relief: 2400,
    insurance_relief_rate: 0.15,
    insurance_relief_cap: 5000,
    prmf_cap: 15000,
    pwd_exemption_monthly_cap: 150000,
  },
};

const effectiveCountry = computed(
  () => config.value?.country || defaultConfig.country,
);

// Convenience computed helpers (merge config with defaults)
const nssfCfg = computed(() => ({
  lower_limit:
    config.value?.nssf?.lower_limit ?? defaultConfig.nssf.lower_limit,
  upper_limit:
    config.value?.nssf?.upper_limit ?? defaultConfig.nssf.upper_limit,
  rate: config.value?.nssf?.rate ?? defaultConfig.nssf.rate,
  employee_cap:
    config.value?.nssf?.employee_cap ?? defaultConfig.nssf.employee_cap,
  employer_match:
    typeof config.value?.nssf?.employer_match === "boolean"
      ? config.value.nssf.employer_match
      : defaultConfig.nssf.employer_match,
}));

const shifCfg = computed(() => ({
  rate: config.value?.shif?.rate ?? defaultConfig.shif.rate,
  min: config.value?.shif?.min ?? defaultConfig.shif.min,
}));

const ahlCfg = computed(() => ({
  rate: config.value?.ahl?.rate ?? defaultConfig.ahl.rate,
}));

const payeCfg = computed(() => {
  const base = defaultConfig.paye;
  const custom = config.value?.paye || {};
  return {
    bands:
      Array.isArray(custom.bands) && custom.bands.length
        ? custom.bands
        : base.bands,
    top_rate: custom.top_rate ?? base.top_rate,
    personal_relief: custom.personal_relief ?? base.personal_relief,
    insurance_relief_rate:
      custom.insurance_relief_rate ?? base.insurance_relief_rate,
    insurance_relief_cap:
      custom.insurance_relief_cap ?? base.insurance_relief_cap,
    prmf_cap: custom.prmf_cap ?? base.prmf_cap,
    pwd_exemption_monthly_cap:
      custom.pwd_exemption_monthly_cap ?? base.pwd_exemption_monthly_cap,
  };
});

onMounted(async () => {
  if (!employeeStore.employees.length) {
    await employeeStore.fetchEmployees({ status: "Active", per_page: 5000 });
  }

  const modalEl = document.getElementById("new-employee-salary");
  modalEl?.addEventListener("shown.bs.modal", async () => {
    await payrollConfigStore.fetchConfig().catch(() => {});
  });

  if (!config.value) {
    await payrollConfigStore.fetchConfig().catch(() => {});
  }

  // Keyboard shortcuts binding scoped to the modal
  bindModalKeyboard();
});

onBeforeUnmount(() => {
  unbindModalKeyboard();
});

// ==== Dynamic lines helpers (with auto-focus) ====
function addExtraAllowance() {
  extraAllowances.value.push({ label: "", amount: 0 });
  nextTick(() => allowanceLabelRefs.value.at(-1)?.focus());
}
function removeExtraAllowance(index) {
  extraAllowances.value.splice(index, 1);
}

function addBenefit() {
  benefits.value.push({ label: "", amount: 0 });
  nextTick(() => benefitLabelRefs.value.at(-1)?.focus());
}
function removeBenefit(index) {
  benefits.value.splice(index, 1);
}

function addExtraDeduction() {
  extraDeductions.value.push({ label: "", amount: 0 });
  nextTick(() => deductionLabelRefs.value.at(-1)?.focus());
}
function removeExtraDeduction(index) {
  extraDeductions.value.splice(index, 1);
}

// Enter on last name adds a new line (super fast data entry)
function onLineEnter(type, index) {
  if (type === "allowance") {
    if (index === extraAllowances.value.length - 1) addExtraAllowance();
    return;
  }
  if (type === "benefit") {
    if (index === benefits.value.length - 1) addBenefit();
    return;
  }
  if (type === "deduction") {
    if (index === extraDeductions.value.length - 1) addExtraDeduction();
  }
}

// Totals for dynamic lines
const extraAllowancesTotal = computed(() =>
  extraAllowances.value.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  ),
);
const benefitsTotal = computed(() =>
  benefits.value.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
);
const extraDeductionsTotal = computed(() =>
  extraDeductions.value.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  ),
);
const prmfTotal = computed(() =>
  extraDeductions.value.reduce((sum, item) => {
    if (item?.p9_type !== "prmf") return sum;
    return sum + (Number(item.amount) || 0);
  }, 0),
);
const postTaxExtraDeductionsTotal = computed(() =>
  Math.max(extraDeductionsTotal.value - prmfTotal.value, 0),
);
const housingBenefitTotal = computed(() =>
  benefits.value.reduce((sum, item) => {
    if (item?.p9_type !== "quarters") return sum;
    return sum + (Number(item.amount) || 0);
  }, 0),
);
const nonCashBenefitsTotal = computed(() =>
  Math.max(benefitsTotal.value - housingBenefitTotal.value, 0),
);

// Hydrate on edit, reset on add
watch(
  () => props.employeeSalary,
  (val) => {
    if (val?.id) {
      const meta = val.meta || {};
      extraAllowances.value = Array.isArray(meta.allowances)
        ? meta.allowances
        : [];
      benefits.value = Array.isArray(meta.benefits) ? meta.benefits : [];
      extraDeductions.value = Array.isArray(meta.deductions)
        ? meta.deductions
        : [];
      const inputVersion = Number(meta.payroll_input_version || 1);

      const storedOther = Number(val.other_allowance) || 0;
      //const storedSacco = Number(val.sacco_loan) || 0;
      const storedOtherLoans = Number(val.other_loans) || 0;

      const extrasForOther =
        inputVersion >= 2
          ? extraAllowancesTotal.value
          : extraAllowancesTotal.value + benefitsTotal.value;
      //const extrasForSacco = extraDeductionsTotal.value;
      const extrasForOtherLoans =
        inputVersion >= 2
          ? postTaxExtraDeductionsTotal.value
          : extraDeductionsTotal.value;

      const baseOther = Math.max(storedOther - extrasForOther, 0);
      //const baseSacco = Math.max(storedSacco - extrasForSacco, 0);
      const baseOtherLoans = Math.max(storedOtherLoans - extrasForOtherLoans, 0);

      form.value = {
        employee_id: val.employee_id ?? val.employee?.id ?? "",
        payroll_month:
          normalizePayrollMonthStart(val.payroll_month) || currentPayrollMonthStart(),
        basic_salary: Number(val.basic_salary) || 0,
        house_allowance: Number(val.house_allowance) || 0,
        transport_allowance: Number(val.transport_allowance) || 0,
        other_allowance: baseOther,
        pension_contrib: Number(val.pension_contrib) || 0,
        insurance_premium: Number(val.insurance_premium) || 0,
        nssf_voluntary: Number(val.nssf_voluntary) || 0,
        mortgage_interest: Number(val.mortgage_interest) || 0,
        //sacco_loan: baseSacco,
        other_loans: baseOtherLoans,
        helb: Number(val.helb) || 0,
        advance_fixed_amount:
          meta.advance_fixed_amount != null && meta.advance_fixed_amount !== ""
            ? Number(meta.advance_fixed_amount)
            : null,
      };
    } else {
      form.value = emptyForm();
      extraAllowances.value = [];
      benefits.value = [];
      extraDeductions.value = [];
    }
  },
  { immediate: true },
);

// Employee options for vue-select
const activeEmployees = computed(() =>
  employees.value.filter(
    (e) => String(e?.status || "").trim().toLowerCase() === "active",
  ),
);

const employeeOptions = computed(() =>
  activeEmployees.value.map((e) => ({
    id: Number(e.id),
    text: `${e.first_name} ${e.last_name || ""} (${e.employee_id})`,
  })),
);

// ===== Payroll calcs =====
const combinedOtherAllowance = computed(
  () =>
    (Number(form.value.other_allowance) || 0) +
    extraAllowancesTotal.value,
);

// const combinedSaccoLoan = computed(
//   () => (Number(form.value.sacco_loan) || 0) + extraDeductionsTotal.value,
// );

const combinedOtherLoans = computed(
  () => (Number(form.value.other_loans) || 0) + postTaxExtraDeductionsTotal.value,
);

// const p9Classifiations = [
//   {
//     id: 1,
//     name: Normal benefit (default),
//   },
//   {
//     id: 2,
//     name:
//   }
// ]

const grossSalary = computed(
  () =>
    (Number(form.value.basic_salary) || 0) +
    (Number(form.value.house_allowance) || 0) +
    (Number(form.value.transport_allowance) || 0) +
    combinedOtherAllowance.value,
);

const storedGrossSalary = grossSalary;

const calculatorGrossSalary = computed(() =>
  toCalculatorAmount(storedGrossSalary.value),
);

const previewGrossSalary = computed(() =>
  toPeriodAmount(storedGrossSalary.value),
);

function computeNssfEmployee(gross) {
  const lower = Number(nssfCfg.value.lower_limit);
  const upper = Number(nssfCfg.value.upper_limit);
  const rate = Number(nssfCfg.value.rate);
  const cap = Number(nssfCfg.value.employee_cap);

  let tier1 = 0;
  let tier2 = 0;

  if (gross <= lower) {
    tier1 = gross * rate;
  } else if (gross <= upper) {
    tier1 = lower * rate;
    tier2 = (gross - lower) * rate;
  } else {
    tier1 = lower * rate;
    tier2 = (upper - lower) * rate;
  }

  const raw = tier1 + tier2;
  return Number(Math.min(raw, cap).toFixed(2));
}

const calculatorNssfEmployee = computed(() =>
  computeNssfEmployee(calculatorGrossSalary.value),
);

const nssfEmployee = computed(() =>
  statutoryToPeriod(calculatorNssfEmployee.value),
);

const calculatorNssfEmployer = computed(() =>
  nssfCfg.value.employer_match ? calculatorNssfEmployee.value : 0,
);

const nssfEmployer = computed(() =>
  statutoryToPeriod(calculatorNssfEmployer.value),
);

const calculatorShif = computed(() => {
  const gross = calculatorGrossSalary.value;
  const rate = Number(shifCfg.value.rate);
  const min = Number(shifCfg.value.min);
  return Number(Math.max(gross * rate, min).toFixed(2));
});

const shif = computed(() => statutoryToPeriod(calculatorShif.value));

const calculatorAhlEmployee = computed(() => {
  const gross = calculatorGrossSalary.value;
  const rate = Number(ahlCfg.value.rate);
  return Number((gross * rate).toFixed(2));
});

const ahlEmployee = computed(() =>
  statutoryToPeriod(calculatorAhlEmployee.value),
);

const calculatorHelb = computed(() => Number(form.value.helb) || 0);
const helb = computed(() => toPeriodAmount(calculatorHelb.value));

const pwdTaxExemptionActive = computed(() => {
  const employee = selectedEmployee.value;
  if (!employee) return false;
  if (employee.disability_status !== "PWD") return false;
  if (!employee.pwd_tax_exemption_verified) return false;
  if (!employee.pwd_tax_exemption_cert_no) return false;

  const period = form.value?.payroll_month
    ? new Date(form.value.payroll_month)
    : new Date();
  const periodStart = new Date(period.getFullYear(), period.getMonth(), 1);
  const periodEnd = new Date(period.getFullYear(), period.getMonth() + 1, 0);

  const certStart = employee.pwd_tax_exemption_start_date
    ? new Date(employee.pwd_tax_exemption_start_date)
    : null;
  const certEnd = employee.pwd_tax_exemption_end_date
    ? new Date(employee.pwd_tax_exemption_end_date)
    : null;

  if (certStart && certStart > periodEnd) return false;
  if (certEnd && certEnd < periodStart) return false;

  return true;
});

const calculatorAllowableDeductions = computed(() => {
  const pension = toCalculatorAmount(form.value.pension_contrib);
  const mortgage = toCalculatorAmount(form.value.mortgage_interest);
  const prmf = toCalculatorAmount(prmfTotal.value);
  return (
    calculatorNssfEmployee.value +
    calculatorShif.value +
    calculatorAhlEmployee.value +
    Math.min(pension, 30000) +
    Math.min(mortgage, 30000) +
    Math.min(prmf, Number(payeCfg.value.prmf_cap || 15000))
  );
});

const calculatorTaxableIncome = computed(() => {
  const taxableEmploymentIncome =
    calculatorGrossSalary.value +
    toCalculatorAmount(nonCashBenefitsTotal.value) +
    toCalculatorAmount(housingBenefitTotal.value);
  const beforePwdExemption = Math.max(
    taxableEmploymentIncome - calculatorAllowableDeductions.value,
    0,
  );
  const pwdCap = Number(payeCfg.value.pwd_exemption_monthly_cap || 150000);
  const pwdExemptIncome = pwdTaxExemptionActive.value
    ? Math.min(beforePwdExemption, pwdCap)
    : 0;

  return Math.max(beforePwdExemption - pwdExemptIncome, 0);
});

const taxableIncome = computed(() =>
  statutoryToPeriod(calculatorTaxableIncome.value),
);

const calculatorPaye = computed(() => {
  let ti = calculatorTaxableIncome.value;
  if (ti <= 0) return 0;

  const {
    bands,
    top_rate,
    personal_relief,
    insurance_relief_rate,
    insurance_relief_cap,
  } = payeCfg.value;

  let tax = 0;
  let remaining = ti;
  let prevLimit = 0;

  if (Array.isArray(bands) && bands.length) {
    for (const band of bands) {
      const limit = Number(band.up_to ?? 0);
      const rate = Number(band.rate ?? 0);
      if (!limit || !rate) continue;
      if (remaining <= 0) break;

      const bandWidth = Math.max(limit - prevLimit, 0);
      const taxableBand = Math.min(remaining, bandWidth);

      if (taxableBand > 0) {
        tax += taxableBand * rate;
        remaining -= taxableBand;
      }

      prevLimit = limit;
    }
  }

  if (remaining > 0 && top_rate != null) {
    tax += remaining * Number(top_rate);
  }

  const insurance = toCalculatorAmount(form.value.insurance_premium);
  const personalRelief = Number(personal_relief);
  const insuranceRelief = Math.min(
    insurance * Number(insurance_relief_rate),
    Number(insurance_relief_cap),
  );
  const totalRelief = personalRelief + insuranceRelief;

  return Math.max(Number((tax - totalRelief).toFixed(2)), 0);
});

const paye = computed(() => statutoryToPeriod(calculatorPaye.value));

const calculatorTotalDeductions = computed(() => {
  const pension = toCalculatorAmount(form.value.pension_contrib);
  const nssfVoluntary = toCalculatorAmount(form.value.nssf_voluntary);
  return (
    calculatorNssfEmployee.value +
    calculatorShif.value +
    calculatorAhlEmployee.value +
    pension +
    nssfVoluntary +
    toCalculatorAmount(prmfTotal.value) +
    calculatorPaye.value +
    toCalculatorAmount(combinedOtherLoans.value) +
    calculatorHelb.value
  );
});

const totalDeductions = computed(() => {
  const pension = toPeriodAmount(form.value.pension_contrib);
  const nssfVoluntary = toPeriodAmount(form.value.nssf_voluntary);
  return (
    nssfEmployee.value +
    shif.value +
    ahlEmployee.value +
    pension +
    nssfVoluntary +
    toPeriodAmount(prmfTotal.value) +
    paye.value +
    toPeriodAmount(combinedOtherLoans.value) +
    helb.value
  );
});

const calculatorNetPay = computed(
  () => calculatorGrossSalary.value - calculatorTotalDeductions.value,
);

const netPay = computed(
  () => previewGrossSalary.value - totalDeductions.value,
);

// ========= Animated numbers (smooth KPI transitions) =========
const animated = reactive({
  gross: 0,
  totalDeductions: 0,
  net: 0,
  nssfEmployee: 0,
  nssfEmployer: 0,
  shif: 0,
  ahlEmployee: 0,
  helb: 0,
  taxableIncome: 0,
  paye: 0,
});

function animateTo(key, to, duration = 420) {
  const from = Number(animated[key] || 0);
  const target = Number(to || 0);

  if (!Number.isFinite(from) || !Number.isFinite(target)) {
    animated[key] = target || 0;
    return;
  }

  const start = performance.now();
  const diff = target - from;

  // If tiny change, snap to avoid jitter
  if (Math.abs(diff) < 0.01) {
    animated[key] = target;
    return;
  }

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const tick = (now) => {
    const t = Math.min((now - start) / duration, 1);
    const eased = easeOutCubic(t);
    animated[key] = from + diff * eased;
    if (t < 1) requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
}

// watch KPI targets (period-scaled preview when applicable)
watch(previewGrossSalary, (v) => animateTo("gross", v));
watch(totalDeductions, (v) => animateTo("totalDeductions", v));
watch(netPay, (v) => animateTo("net", v));
watch(nssfEmployee, (v) => animateTo("nssfEmployee", v));
watch(nssfEmployer, (v) => animateTo("nssfEmployer", v));
watch(shif, (v) => animateTo("shif", v));
watch(ahlEmployee, (v) => animateTo("ahlEmployee", v));
watch(helb, (v) => animateTo("helb", v));
watch(taxableIncome, (v) => animateTo("taxableIncome", v));
watch(paye, (v) => animateTo("paye", v));

onMounted(() => {
  animated.gross = previewGrossSalary.value;
  animated.totalDeductions = totalDeductions.value;
  animated.net = netPay.value;
  animated.nssfEmployee = nssfEmployee.value;
  animated.nssfEmployer = nssfEmployer.value;
  animated.shif = shif.value;
  animated.ahlEmployee = ahlEmployee.value;
  animated.helb = helb.value;
  animated.taxableIncome = taxableIncome.value;
  animated.paye = paye.value;
});

// ========= Validation UX (shake + glow) =========
function highlightInvalidFields() {
  const modal = document.getElementById("new-employee-salary");
  if (!modal) return false;

  let hasAnyInvalid = false;

  // ✅ 1) Validate native required inputs/selects
  const invalids = modal.querySelectorAll(
    "input:required:invalid, select:required:invalid",
  );

  invalids.forEach((el) => {
    hasAnyInvalid = true;
    el.classList.add("dq-invalid");
    el.addEventListener(
      "animationend",
      () => el.classList.remove("dq-invalid"),
      { once: true },
    );
  });

  // ✅ 2) Validate custom Employee dropdown (required)
  const missingEmployee = !form.value.employee_id;
  const ddBtn = modal.querySelector("#employee-dd-btn");

  if (missingEmployee) {
    hasAnyInvalid = true;
    showEmployeeInvalid.value = true;

    // shake/glow the button too
    if (ddBtn) {
      ddBtn.classList.add("dq-invalid");
      ddBtn.addEventListener(
        "animationend",
        () => ddBtn.classList.remove("dq-invalid"),
        { once: true },
      );
      ddBtn.focus();
    }
  } else {
    showEmployeeInvalid.value = false;
  }

  // ✅ Focus first native invalid if employee isn't missing
  if (!missingEmployee && invalids.length) {
    invalids[0]?.focus();
  }

  return hasAnyInvalid;
}

// ========= Keyboard shortcuts =========
let keyHandler = null;

function bindModalKeyboard() {
  const modalEl = document.getElementById("new-employee-salary");
  if (!modalEl) return;

  // Use capture so it works even inside inputs/selects
  keyHandler = (e) => {
    // Only act if modal is actually open (Bootstrap adds .show)
    if (!modalEl.classList.contains("show")) return;

    // ESC -> close
    if (e.key === "Escape") {
      e.preventDefault();
      resetForm();
      closeModal("new-employee-salary");
      return;
    }

    // Ctrl+Enter -> submit
    if (e.ctrlKey && e.key === "Enter") {
      e.preventDefault();
      submitForm();
      return;
    }

    // Ctrl+Shift+A/B/D -> add lines
    if (e.ctrlKey && e.shiftKey) {
      const k = (e.key || "").toLowerCase();
      if (k === "a") {
        e.preventDefault();
        addExtraAllowance();
        return;
      }
      if (k === "b") {
        e.preventDefault();
        addBenefit();
        return;
      }
      if (k === "d") {
        e.preventDefault();
        addExtraDeduction();
        return;
      }
    }
  };

  document.addEventListener("keydown", keyHandler, true);
}

function unbindModalKeyboard() {
  if (keyHandler) document.removeEventListener("keydown", keyHandler, true);
  keyHandler = null;
}

// ========= Submit (Create/Update) =========
async function submitForm() {
  // UX validation: highlight required invalid fields
  if (highlightInvalidFields()) return;

  if (
    advanceCalculationMode.value === "fixed" &&
    (form.value.advance_fixed_amount === null ||
      form.value.advance_fixed_amount === "" ||
      Number.isNaN(Number(form.value.advance_fixed_amount)) ||
      Number(form.value.advance_fixed_amount) < 0)
  ) {
    toast("Enter a fixed advance amount for this employee (use 0 if none).", "error");
    return;
  }

  submitting.value = true;
  try {
    // const cleanLines = (items) =>
    //   items
    //     .map((i) => ({
    //       label: (i.label || "").trim(),
    //       amount: Number(i.amount) || 0,
    //       p9_type: i.p9_type ?? null,
    //     }))
    //     .filter((i) => i.label && i.amount !== 0);
    const cleanLines = (items) =>
      items
        .map((i) => ({
          label: (i.label || "").trim(),
          amount: Number(i.amount) || 0,
          p9_type: i.p9_type === "" ? null : i.p9_type,
        }))
        .filter((i) => i.label && i.amount !== 0);

    const meta = {
      payroll_input_version: 2,
      allowances: cleanLines(extraAllowances.value),
      benefits: cleanLines(benefits.value),
      deductions: cleanLines(extraDeductions.value),
    };

    if (showAdvanceFixedAmount.value) {
      const fixed = form.value.advance_fixed_amount;
      if (fixed !== null && fixed !== "" && !Number.isNaN(Number(fixed))) {
        meta.advance_fixed_amount = Math.max(0, Number(fixed));
      }
    }

    const payload = {
      employee_id: form.value.employee_id,
      basic_salary: form.value.basic_salary,
      house_allowance: form.value.house_allowance,
      transport_allowance: form.value.transport_allowance,
      other_allowance: combinedOtherAllowance.value,
      pension_contrib: form.value.pension_contrib,
      insurance_premium: form.value.insurance_premium,
      nssf_voluntary: form.value.nssf_voluntary,
      mortgage_interest: form.value.mortgage_interest,
      //sacco_loan: combinedSaccoLoan.value,
      other_loans: combinedOtherLoans.value,
      helb: form.value.helb,
      gross_salary: storedGrossSalary.value,
      nssf: calculatorNssfEmployee.value,
      nssf_employer: calculatorNssfEmployer.value,
      shif: calculatorShif.value,
      ahl: calculatorAhlEmployee.value,
      paye: calculatorPaye.value,
      taxable_income: calculatorTaxableIncome.value,
      total_deductions: calculatorTotalDeductions.value,
      net_pay: calculatorNetPay.value,
      // payroll_month: new Date().toISOString().slice(0, 7) + "-01",
      payroll_month: normalizePayrollMonthStart(form.value.payroll_month),

      meta,
    };

    if (isEdit.value) {
      await salaryStore.updateSalary(props.employeeSalary.id, payload);
    } else {
      await salaryStore.createSalary(payload);
    }

    emit("refresh");
    resetForm();
    closeModal("new-employee-salary");
  } catch (e) {
    toast(
      "Error saving salary: " + (e.response?.data?.message || e.message),
      "error",
    );
  } finally {
    submitting.value = false;
  }
}

function resetForm() {
  form.value = emptyForm();
  extraAllowances.value = [];
  benefits.value = [];
  extraDeductions.value = [];
  employeeQuery.value = "";
  showEmployeeInvalid.value = false;
}

function showSalaryModal() {
  const modalEl = document.getElementById("new-employee-salary");
  if (!modalEl) {
    return;
  }
  modalEl.style.removeProperty("display");
  Modal.getOrCreateInstance(modalEl).show();
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (!modalEl) {
    return;
  }

  const instance = Modal.getInstance(modalEl);
  if (instance) {
    instance.hide();
    return;
  }

  modalEl.classList.remove("show");
  modalEl.style.display = "none";
  modalEl.setAttribute("aria-hidden", "true");
  modalEl.removeAttribute("aria-modal");
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

defineExpose({ showSalaryModal });

// ===== Delete support =====
const employeeToDeleteName = computed(() => {
  const emp = props.employeeToDelete?.employee;
  if (!emp) return "";
  return `${emp.first_name} ${emp.last_name || ""} (${emp.employee_id || ""})`;
});

async function confirmDelete() {
  if (!props.employeeToDelete?.id) return;
  deleting.value = true;
  try {
    await salaryStore.deleteSalary(props.employeeToDelete.id);
    emit("refresh");
    closeModal("delete_modal");
  } finally {
    deleting.value = false;
  }
}

// Ensure scrolling is not broken by body overflow lock
watch(
  () =>
    document.getElementById("new-employee-salary")?.classList.contains("show"),
  (open) => {
    if (open) {
      // allow modal to manage scroll; keep body locked but consistent
      document.body.classList.add("modal-open");
    }
  },
  { immediate: true },
);

// ===== Loans/Savings meta engine hint (read-only protection) =====
const metaHint = ref({
  loading: false,
  has_meta: false,
  counts: { loans: 0, savings: 0 },
  active: { loans: [], savings: [] },
});

// const periodForMeta = computed(() => {
//   // You are saving payroll_month as YYYY-MM-01 in payload
//   // so infer YYYY-MM from that
//   const pm = props.employeeSalary?.payroll_month || form.value?.payroll_month;
//   if (pm && typeof pm === "string") return pm.slice(0, 7);

//   // fallback: current month
//   return new Date().toISOString().slice(0, 7);
// });

const periodForMeta = computed(() => {
  const pm = form.value?.payroll_month || props.employeeSalary?.payroll_month;

  if (pm && typeof pm === "string") return pm.slice(0, 7);
  return currentPayrollYYYYMM();
});

const employeeIdForMeta = computed(
  () => Number(form.value.employee_id || 0) || null,
);

const disableLoanSavingsFields = computed(() => !!metaHint.value.has_meta);

async function fetchMetaHint() {
  const employee_id = employeeIdForMeta.value;
  const period = periodForMeta.value;

  if (!employee_id || !period) {
    metaHint.value = {
      loading: false,
      has_meta: false,
      counts: { loans: 0, savings: 0 },
      active: { loans: [], savings: [] },
    };
    return;
  }

  metaHint.value.loading = true;
  try {
    const data = await salaryStore.fetchLoanSavingsMetaHint(
      employee_id,
      period,
    );

    metaHint.value = {
      loading: false,
      has_meta: !!data?.has_meta,
      counts: data?.counts || { loans: 0, savings: 0 },
      active: data?.active || { loans: [], savings: [] },
    };
  } catch (e) {
    metaHint.value.loading = false;
  }
}

// fetch meta when employee changes OR when editing a record
watch([employeeIdForMeta, () => props.employeeSalary?.id], fetchMetaHint, {
  immediate: true,
});
</script>

<style scoped>
/* =========================
   Scroll fix (important)
   ========================= */
.modal-dialog-scrollable .modal-content {
  max-height: calc(100vh - 3.5rem);
}
.modal-dialog-scrollable .modal-body {
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
}

/* Modal shell */
.dq-modal {
  border-radius: 18px;
  overflow: hidden;
  background: #ffffff;
}

.dq-header {
  padding: 18px 20px 14px;
  background: linear-gradient(
    180deg,
    rgba(13, 110, 253, 0.08),
    rgba(255, 255, 255, 0)
  );
}

.dq-footer {
  padding: 14px 20px 18px;
  background: #fff;
}

/* Header icon + close */
.dq-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
  font-size: 20px;
  flex: 0 0 auto;
}

.dq-icon-sm {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
  font-size: 16px;
}

.dq-icon-sm.dq-danger,
.dq-danger {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.dq-close {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: grid;
  place-items: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.dq-close:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
}

/* Premium surfaces */
.dq-payroll-context {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dq-payroll-badge {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.05);
  color: #0f172a;
  font-weight: 600;
}

.dq-payroll-badge-soft {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
}

.dq-payroll-note {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.18);
  background: rgba(59, 130, 246, 0.06);
  color: #334155;
  font-size: 13px;
}

.dq-surface {
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
}

.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.dq-card-head {
  padding: 14px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

/* Section head themes */
.dq-allow {
  background: linear-gradient(
    180deg,
    rgba(255, 102, 0, 0.12),
    rgba(255, 255, 255, 0)
  );
  color: #8a3b00;
}
.dq-benefit {
  background: linear-gradient(
    180deg,
    rgba(13, 110, 253, 0.12),
    rgba(255, 255, 255, 0)
  );
  color: #0b3f9c;
}
.dq-deduct {
  background: linear-gradient(
    180deg,
    rgba(220, 53, 69, 0.12),
    rgba(255, 255, 255, 0)
  );
  color: #7a101c;
}

.dq-card-body {
  padding: 14px;
}

.dq-title {
  font-weight: 800;
  letter-spacing: -0.2px;
  line-height: 1.1;
}
.dq-subtitle {
  font-size: 12px;
  opacity: 0.8;
}

/* Labels + inputs */
.dq-label {
  font-weight: 700;
  font-size: 13px;
  color: #0f172a;
}

.dq-inputgroup .dq-addon {
  background: #fff;
  border-color: rgba(15, 23, 42, 0.12);
  color: #334155;
}

.dq-addon_currency {
  font-size: 12px;
}

.dq-input {
  border-color: rgba(15, 23, 42, 0.12);
  border-radius: 12px;
}
.dq-input:focus {
  border-color: rgba(13, 110, 253, 0.5);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.12);
}

.dq-select {
  border-radius: 12px;
  border-color: rgba(15, 23, 42, 0.12);
}

.dq-block {
  margin-bottom: 12px;
}
.dq-help {
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
}

/* Lines */
.dq-lines-head {
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.dq-lines-title {
  font-weight: 800;
  font-size: 12px;
  color: #0f172a;
}
.dq-lines-hint {
  font-size: 12px;
  color: #64748b;
}

.dq-btn-add {
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  font-weight: 700;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.dq-btn-add:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
}

.dq-line {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.dq-btn-x {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  display: grid;
  place-items: center;
}
.dq-btn-x:hover {
  background: #f8fafc;
}

.dq-mini {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px dashed rgba(15, 23, 42, 0.14);
  border-radius: 14px;
  margin-bottom: 10px;
}

.dq-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

/* Empty state */
.dq-empty {
  padding: 14px;
  border-radius: 16px;
  border: 1px dashed rgba(15, 23, 42, 0.18);
  background: #f8fafc;
  text-align: center;
  color: #334155;
  margin-bottom: 10px;
}
.dq-empty i {
  font-size: 20px;
  opacity: 0.7;
}

/* Callout */
.dq-callout {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 12px;
  border-radius: 16px;
  border: 1px solid rgba(13, 110, 253, 0.18);
  background: rgba(13, 110, 253, 0.06);
  color: #0b3f9c;
}
.dq-callout i {
  margin-top: 2px;
}

/* Pills / badges */
.dq-pill {
  font-size: 12px;
  border-radius: 999px;
  padding: 6px 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.7);
  color: inherit;
  white-space: nowrap;
}

.dq-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.dq-badge {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  color: #0f172a;
  border-radius: 999px;
  padding: 8px 10px;
  font-weight: 700;
}
.dq-badge-success {
  background: rgba(12, 163, 12, 0.08);
  border-color: rgba(12, 163, 12, 0.18);
  color: #0ca30c;
}

/* KPI list */
.dq-kpi-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
}
.dq-kpi-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 700;
}
.dq-kpi-value {
  font-weight: 900;
  color: #0f172a;
}
.dq-kpi-danger .dq-kpi-value {
  color: #dc3545;
}

/* Meta row */
.dq-meta {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.dq-meta-item {
  flex: 1 1 220px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

/* Sticky summary bottom */
.dq-sticky {
  position: sticky;
  bottom: 0;
  z-index: 2;
}
.dq-sticky-inner {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(15, 23, 42, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.dq-sticky-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dq-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #0d6efd;
  box-shadow: 0 0 0 6px rgba(13, 110, 253, 0.12);
}
.dq-sticky-right {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* Sticky top */
.dq-sticky-top {
  position: sticky;
  top: 0;
  z-index: 5;
  margin-bottom: 12px;
}
.dq-sticky-top .dq-sticky-inner {
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(15, 23, 42, 0.12);
  display: flex;
  gap: 8px;
  justify-content: center;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
}

/* Chips */
.dq-chip {
  border-radius: 999px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.1);
  font-weight: 800;
  color: #0f172a;
  font-size: 12px;
}
.dq-chip-red {
  background: rgba(220, 53, 69, 0.06);
  border-color: rgba(220, 53, 69, 0.18);
  color: #7a101c;
}
.dq-chip-green {
  background: rgba(12, 163, 12, 0.06);
  border-color: rgba(12, 163, 12, 0.18);
  color: #0ca30c;
}

/* Buttons */
.dq-btn-primary {
  border-radius: 14px;
  padding: 10px 14px;
  font-weight: 800;
  box-shadow: 0 12px 28px rgba(13, 110, 253, 0.22);
}
.dq-btn-secondary {
  border-radius: 14px;
  padding: 10px 14px;
  font-weight: 800;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
}
.dq-btn-danger {
  border-radius: 14px;
  padding: 10px 14px;
  font-weight: 800;
  box-shadow: 0 12px 28px rgba(220, 53, 69, 0.18);
}

/* Keyboard hint */
.dq-kbd {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.14);
  font-weight: 800;
  font-size: 12px;
  color: #0f172a;
}

/* Invalid input UX */
.dq-invalid {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.15);
  animation: dq-shake 0.35s ease;
}

@keyframes dq-shake {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  50% {
    transform: translateX(4px);
  }
  75% {
    transform: translateX(-3px);
  }
  100% {
    transform: translateX(0);
  }
}

/* Responsive */
@media (max-width: 576px) {
  .dq-line {
    grid-template-columns: 1fr 1fr auto;
  }
  .dq-header {
    padding: 14px 14px 10px;
  }
  .dq-footer {
    padding: 12px 14px 14px;
  }
}

.dq-emp-dd .dq-dd-menu {
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.dq-search-list {
  max-height: 260px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 12px;
}

.dq-dd-item {
  border-radius: 10px;
  padding: 10px 10px;
}

.dq-dd-item:active {
  background: rgba(13, 110, 253, 0.08);
}

.dq-dd-btn {
  border-radius: 12px;
}

/* required validation helper (hidden but still validates) */
.dq-hidden-required {
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
  pointer-events: none;
}

/* HARD SCROLL FIX FOR THIS MODAL ONLY */
#new-employee-salary .modal-dialog {
  height: calc(100vh - 3.5rem);
}

#new-employee-salary .modal-content {
  max-height: calc(100vh - 3.5rem);
  overflow: hidden; /* keep header/footer fixed */
}

#new-employee-salary .modal-body {
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  max-height: calc(100vh - 220px); /* header+footer allowance */
}

/* Employee dropdown invalid style (button) */
.dq-dd-invalid {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.15) !important;
}

.dq-mini-dd {
  min-width: 220px;
  width: 220px;
}

.dq-mini-dd .dq-dd-btn {
  border-radius: 12px;
  border-color: rgba(15, 23, 42, 0.12);
  background: #fff;
}

.dq-mini-dd .dq-dd-menu {
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  z-index: 2000;
}

.dq-mini-dd .dq-dd-item {
  border-radius: 10px;
  padding: 14px 14px;
}
.dq-callout-warn {
  border: 1px solid rgba(255, 193, 7, 0.35);
  background: rgba(255, 193, 7, 0.08);
}
</style>
